import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Package, 
  Settings, 
  RefreshCw,
  Menu,
  X,
  TrendingUp,
  Upload,
  Search,
  AlertCircle,
  BarChart3,
  PieChart,
  LogOut,
  LogIn,
  User,
  Shield,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AskAI } from "@/components/AskAI";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/products", icon: Package, label: "Products" },
  { path: "/bsr", icon: BarChart3, label: "BSR Tracker" },
  { path: "/market-share", icon: PieChart, label: "Market Share" },
  { path: "/watch-groups", icon: Users, label: "Watch Groups" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();

  useEffect(() => {
    fetchStats();
  }, [location.pathname]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.get(`${API}/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.post(`${API}/sync`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      if (response.data.success) {
        toast.success(`Synced ${response.data.synced_count} products`);
        fetchStats();
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to sync products");
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#f2f0ec]">
      {/* Top Header - Dark */}
      <header className="sticky top-0 z-50 bg-[#1a1814] text-white px-4 md:px-6 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md hover:bg-white/10"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-extrabold tracking-tight">
                Price<span className="text-[#f0a500]">Lens</span>
              </h1>
              <span className="hidden sm:inline text-[10px] opacity-50 font-normal ml-1">
                Price Intelligence
              </span>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* BAU Status */}
            <div className={cn(
              "hidden md:flex text-xs px-3 py-1.5 rounded-full border",
              stats?.bau_loaded 
                ? "border-green-500/50 text-green-400 bg-green-500/10"
                : "border-white/15 text-white/60"
            )}>
              {stats?.bau_loaded ? "✓ BAU loaded" : "⚠ No BAU file"}
            </div>

            {/* Sync Button - Admin only */}
            {isAdmin && (
              <Button
                data-testid="sync-all-btn"
                size="sm"
                className="bg-[#f0a500] hover:bg-[#ffc533] text-[#1a1200] font-bold"
                onClick={handleSync}
                disabled={syncing}
              >
                <RefreshCw className={cn("h-4 w-4 mr-1.5", syncing && "animate-spin")} />
                <span className="hidden sm:inline">{syncing ? "Syncing..." : "Sync All"}</span>
              </Button>
            )}

            {/* Search */}
            <div className="hidden md:block relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35" />
              <Input
                data-testid="header-search"
                placeholder="Search product..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 pl-9 bg-white/8 border-white/15 text-white placeholder:text-white/35 text-sm"
              />
            </div>

            {/* User Info & Logout - only when logged in */}
            {isAuthenticated && (
              <div className="flex items-center gap-2">
                {/* Role Badge */}
                <div className={cn(
                  "hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full",
                  isAdmin 
                    ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                    : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                )}>
                  {isAdmin ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                  <span>{isAdmin ? "Admin" : "Viewer"}</span>
                </div>

                {/* Logout Button */}
                <Button
                  data-testid="logout-btn"
                  size="sm"
                  variant="ghost"
                  className="text-white/70 hover:text-white hover:bg-white/10"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed lg:sticky top-[56px] left-0 z-40 w-52 h-[calc(100vh-56px)] transform transition-transform duration-300 ease-in-out lg:translate-x-0",
            "bg-white border-r border-[#e0dbd2] overflow-y-auto",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="py-4">
            {/* Categories Section */}
            <div className="px-4 mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8a8278]">
                Navigation
              </p>
            </div>
            
            <nav className="space-y-0.5 px-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-sm",
                      "hover:bg-[#f2f0ec] border-l-[3px] border-transparent",
                      isActive || (item.path === "/" && location.pathname === "/")
                        ? "bg-[#f2f0ec] font-bold border-l-[#f0a500]"
                        : "text-[#8a8278] hover:text-[#1a1814]"
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}

              {/* Login / Logout — always the last menu option */}
              {isAuthenticated ? (
                <button
                  data-testid="nav-logout"
                  onClick={() => { setSidebarOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-sm hover:bg-[#f2f0ec] border-l-[3px] border-transparent text-[#8a8278] hover:text-[#1a1814]"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout ({isAdmin ? "Admin" : "Viewer"})</span>
                </button>
              ) : (
                <NavLink
                  to="/login"
                  data-testid="nav-login"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-sm",
                      "hover:bg-[#f2f0ec] border-l-[3px] border-transparent",
                      isActive
                        ? "bg-[#f2f0ec] font-bold border-l-[#f0a500]"
                        : "text-[#8a8278] hover:text-[#1a1814]"
                    )
                  }
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </NavLink>
              )}
            </nav>

            <hr className="my-4 mx-4 border-[#e0dbd2]" />

            {/* BAU Alerts */}
            <div className="px-4 mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8a8278]">
                Alerts
              </p>
            </div>
            
            <div className="px-2">
              <div className="flex items-center gap-2.5 px-3 py-2 text-sm text-[#8a8278]">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span>Below BAU</span>
                <span className="ml-auto text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">
                  {stats?.below_bau || 0}
                </span>
              </div>
            </div>

            <hr className="my-4 mx-4 border-[#e0dbd2]" />

            {/* Stats Quick View */}
            <div className="px-4 mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#8a8278]">
                Quick Stats
              </p>
            </div>
            
            <div className="px-4 space-y-2 text-xs text-[#8a8278]">
              <div className="flex justify-between">
                <span>Total Products</span>
                <span className="font-bold text-[#1a1814]">{stats?.total_products || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Price Drops (24h)</span>
                <span className="font-bold text-green-600">{stats?.price_drops_24h || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>At Weekly Low</span>
                <span className="font-bold text-blue-600">{stats?.at_weekly_low || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>At Monthly Low</span>
                <span className="font-bold text-amber-600">{stats?.at_monthly_low || 0}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-[calc(100vh-56px)] overflow-x-auto">
          <div className="p-4 md:p-6">
            <Outlet context={{ searchQuery, stats, refreshStats: fetchStats, isAdmin, user }} />
          </div>
        </main>
      </div>

      {/* Ask AI floating chat */}
      <AskAI />
    </div>
  );
}
